import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { LogIn, UserPlus } from "lucide-react";
import api, { formatApiError } from "../api";
import { useT } from "../i18n";
import { useAuth } from "../context/AuthContext";
import Header from "../components/Header";
import PasswordInput from "../components/PasswordInput";
import ForgotPasswordForm from "../components/ForgotPasswordForm";

const inputCls = "w-full min-h-[48px] rounded-xl border border-[#0D4045]/15 bg-white px-4 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D4045]/30";

export default function LoginPage() {
  const { t } = useT();
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("login");
  const [meta, setMeta] = useState({ specialties: [], communes: [] });
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [regForm, setRegForm] = useState({ name: "", email: "", password: "", specialty: "", commune: "", phone: "", consent: false });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const [inviteToken, setInviteToken] = useState(null);

  useEffect(() => {
    api.get("/meta").then((res) => setMeta(res.data)).catch(() => {});
    const token = searchParams.get("invite");
    if (token) {
      api.get(`/invitations/${token}`)
        .then((res) => {
          setInviteToken(token);
          setTab("register");
          setRegForm((f) => ({ ...f, email: res.data.email }));
        })
        .catch(() => {});
    }
  }, [searchParams]);

  const go = (user) => navigate(user.role === "admin" ? "/admin" : "/espace-pro");

  const submitLogin = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      go(await login(loginForm.email, loginForm.password));
    } catch (err) {
      setError(formatApiError(err) === "Invalid credentials" ? t("login.error") : formatApiError(err));
    } finally { setLoading(false); }
  };

  const submitRegister = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      go(await register({ ...regForm, invite_token: inviteToken }));
    } catch (err) {
      setError(formatApiError(err));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen">
      <Header />
      <main className="max-w-md mx-auto px-4 py-12">
        <h1 className="text-3xl font-extrabold tracking-tight text-[#1F2937]" style={{ fontFamily: "Manrope" }}>{t("login.title")}</h1>
        <p className="text-base text-[#6B7280] mt-2">{t("login.subtitle")}</p>

        <div className="flex gap-2 mt-6" role="tablist">
          {[["login", t("login.tab.login")], ["register", t("login.tab.register")]].map(([key, label]) => (
            <button key={key} data-testid={`login-tab-${key}`} onClick={() => { setTab(key); setError(""); }}
              className={`flex-1 min-h-[48px] rounded-full text-sm font-semibold border transition-colors duration-200 ${
                tab === key ? "bg-[#0D4045] text-[#FDFBF7] border-[#0D4045]" : "bg-white text-[#0D4045] border-[#0D4045]/15"
              }`}>
              {label}
            </button>
          ))}
        </div>

        {inviteToken && (
          <p data-testid="invite-notice" className="mt-4 text-sm font-semibold text-[#065F46] bg-[#D1FAE5] border border-[#34D399]/50 rounded-xl px-4 py-3">
            {t("login.invited")}
          </p>
        )}
        {error && <p data-testid="login-error" className="mt-4 text-sm font-semibold text-[#991B1B] bg-[#FEE2E2] border border-[#F87171]/50 rounded-xl px-4 py-3">{error}</p>}

        {tab === "login" ? (
          <form onSubmit={submitLogin} className="flex flex-col gap-4 mt-6" data-testid="login-form">
            <input data-testid="login-email-input" type="email" required placeholder={t("login.email")}
              value={loginForm.email} onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })} className={inputCls} />
            <PasswordInput value={loginForm.password}
              onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
              testid="login-password-input" toggleTestid="login-toggle-password" />
            <button type="submit" disabled={loading} data-testid="login-submit-btn"
              className="flex items-center justify-center gap-2 min-h-[48px] bg-[#0D4045] text-[#FDFBF7] font-semibold rounded-full hover:bg-[#0a3237] transition-colors duration-200 disabled:opacity-60">
              <LogIn size={16} /> {loading ? "…" : t("login.submit")}
            </button>
          </form>
        ) : (
          <></>
        )}

        {tab === "login" && <ForgotPasswordForm />}

        {tab === "register" && (
          <form onSubmit={submitRegister} className="flex flex-col gap-4 mt-6" data-testid="register-form">
            <input data-testid="register-name-input" required placeholder={t("login.name")}
              value={regForm.name} onChange={(e) => setRegForm({ ...regForm, name: e.target.value })} className={inputCls} />
            <input data-testid="register-email-input" type="email" required placeholder={t("login.email")}
              value={regForm.email} onChange={(e) => setRegForm({ ...regForm, email: e.target.value })} className={inputCls} />
            <PasswordInput value={regForm.password} minLength={8}
              onChange={(e) => setRegForm({ ...regForm, password: e.target.value })}
              testid="register-password-input" toggleTestid="register-toggle-password" />
            <select data-testid="register-specialty-select" required value={regForm.specialty}
              onChange={(e) => setRegForm({ ...regForm, specialty: e.target.value })} className={inputCls}>
              <option value="">{t("filters.specialty")}…</option>
              {meta.specialties.map((s) => <option key={s} value={s}>{t(`specialty.${s}`)}</option>)}
            </select>
            <select data-testid="register-commune-select" required value={regForm.commune}
              onChange={(e) => setRegForm({ ...regForm, commune: e.target.value })} className={inputCls}>
              <option value="">{t("filters.commune")}…</option>
              {meta.communes.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <input data-testid="register-phone-input" placeholder={t("login.phone")}
              value={regForm.phone} onChange={(e) => setRegForm({ ...regForm, phone: e.target.value })} className={inputCls} />
            <label className="flex items-start gap-2.5 text-xs text-[#1F2937] cursor-pointer" data-testid="register-consent-label">
              <input type="checkbox" required data-testid="register-consent-checkbox" className="mt-0.5 accent-[#0D4045]"
                checked={regForm.consent}
                onChange={(e) => setRegForm({ ...regForm, consent: e.target.checked })} />
              {t("register.consent")}
            </label>
            <p className="text-xs text-[#6B7280]">{t("login.register.notice")}</p>
            <button type="submit" disabled={loading} data-testid="register-submit-btn"
              className="flex items-center justify-center gap-2 min-h-[48px] bg-[#0D4045] text-[#FDFBF7] font-semibold rounded-full hover:bg-[#0a3237] transition-colors duration-200 disabled:opacity-60">
              <UserPlus size={16} /> {loading ? "…" : t("login.register.submit")}
            </button>
          </form>
        )}
      </main>
    </div>
  );
}
